import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { PaginatedResponse } from '@interfaces/machines';
import { ClampingTool, NCFileSearchItem, PartSearchItem, ProductionBatch } from '@interfaces/search';
import { useDispatch, useSelector } from 'react-redux';
import { setBatchList } from '@reducers/batch';
import { selectForceUpdateHeaderConfig, setNcFileData, setNcFileId, setNcFiles, setParts, setPartId } from '@reducers/details';
import { GetBatchesByEntityId } from '@interfaces/methods';
import { parseRouteParamAsNumber } from '@helpers';

interface UseToolDetailsProps {
  getPartByID?: (partId: number) => Promise<PaginatedResponse<PartSearchItem>>;
  getProductionBatchByPart?: GetBatchesByEntityId;
  getNCFileById?: (ncFileId: string | number, select?: string[] | null) => Promise<NCFileSearchItem>;
  getToolById: (id: number) => Promise<ClampingTool[]>;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  getFullNCFile?: boolean;
}

export const useToolDetails = ({
  getProductionBatchByPart,
  getNCFileById,
  getToolById,
  updateHeaderConfig,
  getPartByID,
  getFullNCFile,
}: UseToolDetailsProps) => {
  const route = useRoute();
  const dispatch = useDispatch();

  const toolId = parseRouteParamAsNumber(route.params?.toolId);

  const toolIdRef = useRef<number | null>(null);

  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(true);
  const [partDataLoading, setPartDataLoading] = useState<boolean>(true);

  const loadAndSetBatches = async () => {
    if (!getProductionBatchByPart) return;

    const batches = getProductionBatchByPart ? await getProductionBatchByPart({ id: toolId }) : undefined;
    if (batches?.searchResult?.length) {
      dispatch(setBatchList(batches.searchResult));
    }
    return batches;
  };

  const loadAndSetPartDetails = async () => {
    if (!getPartByID) {
      setPartDataLoading(false);
      return;
    }

    const partDetails = await getPartByID(toolId);
    if (partDetails.searchResult.length) {
      dispatch(setParts([partDetails.searchResult[0]]));
      dispatch(setPartId(partDetails.searchResult?.[0]?.partId));
    }
    setPartDataLoading(false);
  };

  const loadAndSetNCFile = async (ncFileId: string | number) => {
    if (!getNCFileById || ncFileId == null) {
      setNcFileDataLoading(false);
      return;
    }

    const ncFilesData = await getNCFileById(ncFileId, !getFullNCFile ? ['NCFileName', 'NCFileId', 'NCFileStatus'] : null);
    if (ncFilesData && getFullNCFile) {
      dispatch(setNcFileId(ncFilesData.ncFileId));
      dispatch(setNcFileData(ncFilesData));
    }
    setNcFileDataLoading(false);
    return ncFilesData;
  };

  const loadDataAndDefineHeader = async () => {
    // await loadAndSetPartDetails();
    // const batchesData = await loadAndSetBatches();

    toolIdRef.current = toolId;
    const toolData = await getToolById(toolId);
    const ncFilesData = await loadAndSetNCFile(toolData?.[0]?.ncFileId);

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: '#0',
        batch: '#0',
        ncFile: ncFilesData?.NcFileName ?? ncFilesData?.ncFileName ?? '',
        ncFileId: ncFilesData?.NcFileId ?? ncFilesData?.ncFileId ?? '',
        ncFileStatus: ncFilesData?.NcFileStatus ?? ncFilesData?.ncFileStatus ?? '',
        part: {
          id: '',
          name: '',
        },
        tools: route.params?.toolName ?? toolData?.[0]?.toolName ?? '',
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [toolId, forceUpdateHeaderConfig])
  );

  const refetchTools = useCallback(() => {
    if (toolIdRef.current) {
      getToolById(toolIdRef.current);
    }
  }, [getToolById]);

  return { ncFileDataLoading, partDataLoading, refetchTools };
};
